import React, { useState, useEffect } from 'react';
import {
    DndContext,
    closestCenter,
    PointerSensor,
    KeyboardSensor,
    useSensor,
    useSensors,
    DragOverlay
} from '@dnd-kit/core';
import Globals from '../../config/globals';
import { sortableKeyboardCoordinates } from '@dnd-kit/sortable';
import { useUserDashboard, useUpdateDashboardLayout } from '../../hooks/useDashboard';
import { useWidgetCountsStream } from '../../hooks/useWidgetData';
import DroppableColumn from './DroppableColumn';
import DashboardWidget from './DashboardWidget';
import WidgetPicker from './WidgetPicker';
import { Plus, Settings } from 'lucide-react';

/**
 * Dashboard component
 * Main container for draggable widget dashboard
 */
export default function Dashboard() {
const [showWidgetPicker, setShowWidgetPicker] = useState(false);
const [activeGlobalId, setActiveGlobalId] = useState(null);
const [activeReportId, setActiveReportId] = useState(null);
const [theme, setTheme] = useState(window.sessionStorage.getItem("theme") || Globals.theme || "light");
const editMode = true; // Could be controlled by useState for toggle

const { data: dashboard, isLoading } = useUserDashboard();
const updateLayout = useUpdateDashboardLayout();
useWidgetCountsStream();

// Sync with app's dark mode system
useEffect(() => {
    const currentTheme = window.sessionStorage.getItem("theme") || Globals.theme || "light";
    setTheme(currentTheme);

    // Apply Tailwind's dark class to document root for dark: prefix support
    if (currentTheme === "dark") {
        document.documentElement.classList.add("dark");
    } else {
        document.documentElement.classList.remove("dark");
    }
}, []);

// Watch for theme changes from other components
useEffect(() => {
    const interval = setInterval(() => {
        const currentTheme = window.sessionStorage.getItem("theme") || Globals.theme || "light";
        if (currentTheme !== theme) {
            setTheme(currentTheme);
            if (currentTheme === "dark") {
                document.documentElement.classList.add("dark");
            } else {
                document.documentElement.classList.remove("dark");
            }
        }
    }, 500);

    return () => clearInterval(interval);
}, [theme]);

    const sensors = useSensors(
        useSensor(PointerSensor, {
            activationConstraint: {
                distance: 8
            }
        }),
        useSensor(KeyboardSensor, {
            coordinateGetter: sortableKeyboardCoordinates
        })
    );

    const handleGlobalDragStart = (event) => {
        setActiveGlobalId(event.active.id);
    };

    const handleGlobalDragEnd = async (event) => {
        const { active, over } = event;
        setActiveGlobalId(null);

        if (!over) {
            return;
        }

        const widgets = dashboard?.widgets || [];
        const reportWidgets = widgets.filter(w =>
            w.widgetDefinition?.widgetCategory !== 'system' && w.widgetInstanceKey !== null
        );

        // Fix 3: rebuild post-processed column arrays (same logic as render) so all
        // lookups are against the columns the user actually sees, not stale API data.
        const placedSystem = widgets.filter(w =>
            w.widgetDefinition?.widgetCategory === 'system' && w.widgetInstanceKey !== null
        );
        const unplacedSystem = widgets
            .filter(w => w.widgetDefinition?.widgetCategory === 'system' && w.widgetInstanceKey === null)
            .map(w => ({ ...w, widgetInstanceKey: `sys-${w.widgetDefinitionId}` }));

        const column0Widgets = [
            ...placedSystem.filter(w => w.columnPosition === 0).sort((a, b) => a.rowPosition - b.rowPosition)
        ];
        const column1Widgets = [
            ...placedSystem.filter(w => w.columnPosition === 1).sort((a, b) => a.rowPosition - b.rowPosition)
        ];
        unplacedSystem.forEach((w, i) => {
            if (i % 2 === 0) column0Widgets.push(w);
            else column1Widgets.push(w);
        });

        // Locate the dragged widget by index in the post-processed columns.
        const draggedInCol0Idx = column0Widgets.findIndex(w => w.widgetInstanceKey === active.id);
        const draggedFromCol = draggedInCol0Idx !== -1 ? 0 : 1;
        const draggedRowIdx = draggedFromCol === 0
            ? draggedInCol0Idx
            : column1Widgets.findIndex(w => w.widgetInstanceKey === active.id);
        const draggedWidget = draggedFromCol === 0
            ? column0Widgets[draggedInCol0Idx]
            : column1Widgets[draggedRowIdx];

        if (!draggedWidget) {
            return;
        }

        // Determine target column and row index.
        let targetColumn;
        let targetRow;

        const overIdString = String(over.id);

        if (overIdString.startsWith('global-column-')) {
            if (overIdString.endsWith('-bottom')) {
                const parts = overIdString.split('-');
                targetColumn = Number.parseInt(parts[2]);
                const targetColWidgets = targetColumn === 0 ? column0Widgets : column1Widgets;
                targetRow = draggedFromCol === targetColumn
                    ? targetColWidgets.length - 1
                    : targetColWidgets.length;
            } else {
                const parts = overIdString.split('-');
                targetColumn = Number.parseInt(parts[2]);
                targetRow = 0;
            }
        } else {
            // Dropped on another widget � resolve by index in post-processed columns.
            const targetInCol0Idx = column0Widgets.findIndex(w => w.widgetInstanceKey === over.id);
            if (targetInCol0Idx !== -1) {
                targetColumn = 0;
                targetRow = targetInCol0Idx;
            } else {
                const targetInCol1Idx = column1Widgets.findIndex(w => w.widgetInstanceKey === over.id);
                if (targetInCol1Idx === -1) {
                    return;
                }
                targetColumn = 1;
                targetRow = targetInCol1Idx;
            }
        }

        // If dropping on itself, do nothing.
        if (draggedFromCol === targetColumn && draggedRowIdx === targetRow) {
            return;
        }

        // Remove from source column, insert into target column.
        const sourceColumn = draggedFromCol === 0 ? column0Widgets : column1Widgets;
        sourceColumn.splice(draggedRowIdx, 1);

        const destColumn = targetColumn === 0 ? column0Widgets : column1Widgets;
        destColumn.splice(targetRow, 0, draggedWidget);

        // Rebuild with updated positions.
        const updatedGlobalWidgets = [];
        let globalOrder = 0;

        column0Widgets.forEach((widget, rowIdx) => {
            updatedGlobalWidgets.push({
                ...widget,
                columnPosition: 0,
                rowPosition: rowIdx,
                displayOrder: globalOrder++
            });
        });

        column1Widgets.forEach((widget, rowIdx) => {
            updatedGlobalWidgets.push({
                ...widget,
                columnPosition: 1,
                rowPosition: rowIdx,
                displayOrder: globalOrder++
            });
        });

        const allUpdatedWidgets = [...updatedGlobalWidgets, ...reportWidgets];

        try {
            const layoutPayload = {
                widgets: allUpdatedWidgets.map(w => ({
                    userDashboardWidgetId: w.userDashboardWidgetId,
                    widgetDefinitionId: w.widgetDefinitionId,
                    displayOrder: w.displayOrder,
                    rowPosition: w.rowPosition,
                    columnPosition: w.columnPosition,
                    columnSpan: w.columnSpan,
                    isCollapsed: w.isCollapsed
                }))
            };

            await updateLayout.mutateAsync(layoutPayload);
        } catch (error) {
            console.error('Error updating layout:', error);
        }
    };

    const handleReportDragStart = (event) => {
        setActiveReportId(event.active.id);
    };

    const handleReportDragEnd = async (event) => {
        const { active, over } = event;
        setActiveReportId(null);

        if (!over) {
            return;
        }

        const widgets = dashboard?.widgets || [];
        const globalWidgets = widgets.filter(w => w.widgetDefinition?.widgetCategory === 'system' && w.widgetInstanceKey !== null);
        const reportWidgets = widgets.filter(w => w.widgetDefinition?.widgetCategory !== 'system' && w.widgetInstanceKey !== null);
        
        const draggedWidget = reportWidgets.find(w => w.widgetInstanceKey === active.id);
        
        if (!draggedWidget) {
            return;
        }

        // Determine target column and position
        let targetColumn;
        let targetRow;

        const overIdString = String(over.id);

        // Check if dropped on a column zone (empty column or bottom zone) or a widget
        if (overIdString.startsWith('report-column-')) {
            // Dropped on a column zone
            if (overIdString.endsWith('-bottom')) {
                // Dropped on bottom zone
                const parts = overIdString.split('-');
                targetColumn = Number.parseInt(parts[2]);
                
                // Get widgets in target column
                const targetColumnWidgets = targetColumn === 0 ? 
                    reportWidgets.filter(w => w.columnPosition === 0).sort((a, b) => a.rowPosition - b.rowPosition) :
                    reportWidgets.filter(w => w.columnPosition === 1).sort((a, b) => a.rowPosition - b.rowPosition);
                
                // Calculate target row accounting for same-column vs cross-column moves
                if (draggedWidget.columnPosition === targetColumn) {
                    // Same column: widget will be removed first, so length - 1
                    targetRow = targetColumnWidgets.length - 1;
                } else {
                    // Different column: target column keeps full length
                    targetRow = targetColumnWidgets.length;
                }
            } else {
                // Dropped on empty column's main droppable area
                const parts = overIdString.split('-');
                targetColumn = Number.parseInt(parts[2]);
                targetRow = 0; // Place at top of empty column
            }
        } else {
            // Dropped on another widget
            const targetWidget = reportWidgets.find(w => w.widgetInstanceKey === over.id);
            if (!targetWidget) {
                return;
            }
            targetColumn = targetWidget.columnPosition;
            targetRow = targetWidget.rowPosition;
        }

        // If dropping on itself, do nothing
        if (draggedWidget.columnPosition === targetColumn && 
            draggedWidget.rowPosition === targetRow) {
            return;
        }

        // Separate widgets by column
        const column0Widgets = reportWidgets
            .filter(w => w.columnPosition === 0)
            .sort((a, b) => a.rowPosition - b.rowPosition);
        
        const column1Widgets = reportWidgets
            .filter(w => w.columnPosition === 1)
            .sort((a, b) => a.rowPosition - b.rowPosition);

        // Remove dragged widget from its source column
        let sourceColumn = draggedWidget.columnPosition === 0 ? column0Widgets : column1Widgets;
        const sourceIndex = sourceColumn.findIndex(w => w.widgetInstanceKey === draggedWidget.widgetInstanceKey);
        sourceColumn.splice(sourceIndex, 1);

        // Insert into target column at target position
        let destColumn = targetColumn === 0 ? column0Widgets : column1Widgets;
        destColumn.splice(targetRow, 0, draggedWidget);

        // Rebuild report widget list with updated positions
        const updatedReportWidgets = [];
        let reportOrder = 0;

        // Process column 0
        column0Widgets.forEach((widget, rowIdx) => {
            updatedReportWidgets.push({
                ...widget,
                columnPosition: 0,
                rowPosition: rowIdx,
                displayOrder: reportOrder++,
                isPersisted: widget.userDashboardWidgetId != null
            });
        });

        // Process column 1
        column1Widgets.forEach((widget, rowIdx) => {
            updatedReportWidgets.push({
                ...widget,
                columnPosition: 1,
                rowPosition: rowIdx,
                displayOrder: reportOrder++,
                isPersisted: widget.userDashboardWidgetId != null
            });
        });

        // Combine with unchanged global widgets
        const allUpdatedWidgets = [...globalWidgets, ...updatedReportWidgets];

        try {
            const layoutPayload = {
                widgets: allUpdatedWidgets.map(w => ({
                    userDashboardWidgetId: w.userDashboardWidgetId,
                    widgetDefinitionId: w.widgetDefinitionId,
                    displayOrder: w.displayOrder,
                    rowPosition: w.rowPosition,
                    columnPosition: w.columnPosition,
                    columnSpan: w.columnSpan,
                    isCollapsed: w.isCollapsed
                }))
            };

            await updateLayout.mutateAsync(layoutPayload);
        } catch (error) {
            console.error('Error updating layout:', error);
        }
    };

    if (isLoading) {
        return (
            <div className="flex items-center justify-center min-h-screen">
                <div className="text-gray-500">Loading dashboard...</div>
            </div>
        );
    }

    const widgets = dashboard?.widgets || [];
    
    // Separate global and report widgets
    // Split system widgets into placed (real GUID key) and unplaced (null key ? fallback).
    const placedSystemWidgets = widgets.filter(w =>
        w.widgetDefinition?.widgetCategory === 'system' && w.widgetInstanceKey !== null
    );
    const unplacedSystemWidgets = widgets
        .filter(w => w.widgetDefinition?.widgetCategory === 'system' && w.widgetInstanceKey === null)
        .map(w => ({ ...w, widgetInstanceKey: `sys-${w.widgetDefinitionId}` }));
    // Full normalised system array � used for section-visibility guard and DragOverlay lookup.
    const globalWidgets = [...placedSystemWidgets, ...unplacedSystemWidgets];

    // User widgets: only placed entries (widgetInstanceKey !== null)
    const reportWidgets = widgets.filter(w =>
        w.widgetDefinition?.widgetCategory !== 'system' && w.widgetInstanceKey !== null
    );

    // Fix 2: placed widgets go into their persisted columns; unplaced are distributed
    // round-robin so every system widget is always visible.
    const globalColumn0Widgets = [
        ...placedSystemWidgets.filter(w => w.columnPosition === 0).sort((a, b) => a.rowPosition - b.rowPosition)
    ];
    const globalColumn1Widgets = [
        ...placedSystemWidgets.filter(w => w.columnPosition === 1).sort((a, b) => a.rowPosition - b.rowPosition)
    ];
    unplacedSystemWidgets.forEach((w, i) => {
        if (i % 2 === 0) globalColumn0Widgets.push(w);
        else globalColumn1Widgets.push(w);
    });
    
    // Report widget columns
    const reportColumn0Widgets = reportWidgets
        .filter(w => w.columnPosition === 0)
        .sort((a, b) => a.rowPosition - b.rowPosition);
    
    const reportColumn1Widgets = reportWidgets
        .filter(w => w.columnPosition === 1)
        .sort((a, b) => a.rowPosition - b.rowPosition);

    return (
        <div className={`p-6 min-h-screen ${theme === "dark" ? "dark-theme" : ""}`} style={{
            backgroundColor: theme === 'dark' ? '#18181b' : '#fafafa'
        }}>

            {/* Empty State */}
            {widgets.length === 0 && (
                <div className="flex flex-col items-center justify-center py-20 rounded-xl border-2 border-dashed" style={{
                    backgroundColor: theme === 'dark' ? '#2b2b2b' : '#fdf2f8',
                    borderColor: theme === 'dark' ? 'rgba(236, 72, 153, 0.5)' : '#f9a8d4'
                }}>
                    <div className="mb-4" style={{
                        color: theme === 'dark' ? '#71717a' : '#ec4899'
                    }}>
                        <Plus className="w-16 h-16 mx-auto" />
                    </div>
                    <h3 className="text-xl font-semibold mb-2" style={{
                        color: theme === 'dark' ? '#e4e4e7' : '#9f1239'
                    }}>
                        Your dashboard is empty
                    </h3>
                    <p className="mb-6" style={{
                        color: theme === 'dark' ? '#a1a1aa' : '#831843'
                    }}>
                        Add widgets to get started with your personalized dashboard
                    </p>
                    <button
                        className="inline-flex items-center gap-2 px-6 py-3 rounded-lg transition-colors font-medium"
                        style={{
                            backgroundColor: theme === 'dark' ? '#ec4899' : '#db2777',
                            color: 'white'
                        }}
                        onClick={() => setShowWidgetPicker(true)}
                        onMouseEnter={(e) => e.currentTarget.style.backgroundColor = theme === 'dark' ? '#db2777' : '#be185d'}
                        onMouseLeave={(e) => e.currentTarget.style.backgroundColor = theme === 'dark' ? '#ec4899' : '#db2777'}
                    >
                        <Plus className="w-4 h-4" />
                        Add Your First Widget
                    </button>
                </div>
            )}

            {/* Global Widgets Section */}
            {globalWidgets.length > 0 && (
                <div className="mb-8">
                    <h2 className="text-2xl font-semibold mb-4" style={{
                        color: theme === 'dark' ? '#ec4899' : '#db2777'
                    }}>
                        System Widgets
                    </h2>
                    <div className="mb-6" style={{
                        height: '2px',
                        background: theme === 'dark' ? 'rgba(236, 72, 153, 0.3)' : 'rgba(219, 39, 119, 0.3)'
                    }}></div>
                    <DndContext
                        sensors={sensors}
                        collisionDetection={closestCenter}
                        onDragStart={handleGlobalDragStart}
                        onDragEnd={handleGlobalDragEnd}
                    >
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                            <DroppableColumn
                                id="global-column-0"
                                widgets={globalColumn0Widgets}
                                editMode={editMode}
                                isEmpty={globalColumn0Widgets.length === 0}
                                staggerOffset={0}
                            />
                            <DroppableColumn
                                id="global-column-1"
                                widgets={globalColumn1Widgets}
                                editMode={editMode}
                                isEmpty={globalColumn1Widgets.length === 0}
                                staggerOffset={globalColumn0Widgets.length}
                            />
                        </div>

                        <DragOverlay dropAnimation={null}>
                            {activeGlobalId && globalWidgets.find(w => w.widgetInstanceKey === activeGlobalId) ? (
                                <div className="cursor-grabbing opacity-50">
                                    <DashboardWidget
                                        widget={globalWidgets.find(w => w.widgetInstanceKey === activeGlobalId)}
                                        editMode={editMode}
                                    />
                                </div>
                            ) : null}
                        </DragOverlay>
                    </DndContext>
                </div>
            )}

            {/* Report Widgets Section */}
            {/* Fix 5: header + Add Widget button always render; drag grid or empty state below */}
            <div className="mb-8">
                <div className="flex items-center gap-2 mb-4">
                    <h2 
                        className="text-2xl font-semibold"
                        style={{
                            color: theme === 'dark' ? '#ec4899' : '#db2777'
                        }}
                    >
                        My Widgets
                    </h2>
                    <div 
                        className="flex-1"
                        style={{
                            height: '2px',
                            background: theme === 'dark' ? 'rgba(236, 72, 153, 0.3)' : 'rgba(219, 39, 119, 0.3)'
                        }}
                    />
                    <button
                        className="inline-flex items-center gap-2 px-4 py-2 rounded-lg transition-colors font-medium"
                        style={{
                            backgroundColor: theme === 'dark' ? '#ec4899' : '#db2777',
                            color: 'white'
                        }}
                        onClick={() => setShowWidgetPicker(true)}
                        onMouseEnter={(e) => e.currentTarget.style.backgroundColor = theme === 'dark' ? '#db2777' : '#be185d'}
                        onMouseLeave={(e) => e.currentTarget.style.backgroundColor = theme === 'dark' ? '#ec4899' : '#db2777'}
                    >
                        <Plus className="w-4 h-4" />
                        Add Widget
                    </button>
                </div>
                {reportWidgets.length > 0 ? (
                    <DndContext
                        sensors={sensors}
                        collisionDetection={closestCenter}
                        onDragStart={handleReportDragStart}
                        onDragEnd={handleReportDragEnd}
                    >
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                            <DroppableColumn
                                id="report-column-0"
                                widgets={reportColumn0Widgets}
                                editMode={editMode}
                                isEmpty={reportColumn0Widgets.length === 0}
                            />
                            <DroppableColumn
                                id="report-column-1"
                                widgets={reportColumn1Widgets}
                                editMode={editMode}
                                isEmpty={reportColumn1Widgets.length === 0}
                            />
                        </div>

                        <DragOverlay dropAnimation={null}>
                            {activeReportId && reportWidgets.find(w => w.widgetInstanceKey === activeReportId) ? (
                                <div className="cursor-grabbing opacity-50">
                                    <DashboardWidget
                                        widget={reportWidgets.find(w => w.widgetInstanceKey === activeReportId)}
                                        editMode={editMode}
                                    />
                                </div>
                            ) : null}
                        </DragOverlay>
                    </DndContext>
                ) : (
                    <div
                        className="flex items-center justify-center py-12 rounded-lg border-2 border-dashed"
                        style={{
                            borderColor: theme === 'dark' ? '#3f3f46' : '#e5e7eb',
                            color: theme === 'dark' ? '#71717a' : '#9ca3af'
                        }}
                    >
                        No widgets added yet. Click "Add Widget" to get started.
                    </div>
                )}
            </div>

            {/* Widget Picker */}
            <WidgetPicker open={showWidgetPicker} onOpenChange={setShowWidgetPicker} dashboard={dashboard} />
        </div>
    );
}